import * as React from "react";
import LandingCard from "./landing_card";
import { Typography, Stack, Box, ButtonBase, Button, Divider, Skeleton } from "@mui/material";
import { REACT_QUERY_KEYS } from "../../../enums/react_query_key_enum";
import { fetchSiteStatistics } from "../../../scripts/api_v2/landing_page_client";
import { useQuery } from "@tanstack/react-query";
import useBackend, { useLogin } from "../../../hooks/use_backend";
import { useAmplitudeTrack, useOpenSite, SITE_SELECTION_SOURCE } from "../../../scripts/amplitude_utils";
import { TOOLTIPS_EN } from "../../../enums/tooltip_enum_EN";

function StatisticsCard({ title, metric, unit, ...props }) {
  const backend = useBackend();
  const login = useLogin();
  const [count, setCount] = React.useState(5);
  const { trackButtonClicked } = useAmplitudeTrack();

  let { data: data, isLoading: fetchingSiteStatisticsData } = useQuery({
    queryKey: [REACT_QUERY_KEYS.LANDING_PAGE_SITE_STATISTICS, backend.fetch, login.company_name, count, metric],
    queryFn: () => fetchSiteStatistics(backend.fetch, count, metric),
    refetchInterval: 60000,
    placeholderData: (previousData, previousQuery) => previousData,
  });

  React.useEffect(() => {
    data && setCount(Math.min(data?.total, count)); // count cannot be larger than data
  }, [data]);

  return (
    <LandingCard
      title={title}
      tooltip={TOOLTIPS_EN.CREDIT_LINE_PER_KW}
      {...props}
      sx={{
        display: "flex",
        flexDirection: "column",
        ...props.sx,
      }}
    >
      <Stack spacing={0.5} justifyContent={"space-around"} direction="row" sx={{ mb: 2, mt: 1 }}>
        <LegendValue name="MINIMUM" unit={unit} value={data?.min?.toFixed(2)} />

        <LegendValue name="AVERAGE" unit={unit} value={data?.mean?.toFixed(2)} />

        <LegendValue name="MAXIMUM" unit={unit} value={data?.max?.toFixed(2)} />
      </Stack>

      <Stack spacing={0.5}>
        {[...Array(count)].map((_, index) => {
          const isLoaded = data && index < data.list.length;

          if (isLoaded) {
            return (
              <SiteItem
                key={data.list[index].site_id}
                siteId={data.list[index].site_id}
                siteName={data.list[index].site_name}
                description={data.list[index].description}
                value={data.list[index].value}
                unit={unit}
                isLast={index === count - 1}
                isLoading={false}
              />
            );
          }
          return <SiteItem key={index} isLoading={true} />;
        })}
      </Stack>

      <Button sx={{ marginTop: 1 }} onClick={() => {
          trackButtonClicked('statistics_view_more', 'landing_page', { shown_count: count + 5 });
          setCount(count + 5);
        }}>
        View more
      </Button>
    </LandingCard>
  );
}

export default StatisticsCard;

function SiteItem({ siteName, siteId, value, description, unit, isLoading, isLast }) {
  const openSite = useOpenSite();
  return [
    <Divider key="divider" />,
    <ButtonBase
      key="button"
      sx={{
        width: "100%",
        alignItems: "center",
        display: "flex",
        textAlign: "left",
        borderRadius: "5px",
        px: 1,
        py: 0.5,
        "&:hover": { backgroundColor: "#f3f3f3" },
      }}
      disabled={isLoading}
      onClick={() => openSite(siteId, SITE_SELECTION_SOURCE.STATISTICS_CARD)}
    >
      <Stack sx={{ width: "calc(100% - 100px)", marginRight: "10px" }}>
        <Typography variant="body2" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {isLoading ? <Skeleton /> : siteName}
        </Typography>
        <Typography variant="caption">{isLoading ? <Skeleton /> : description}</Typography>
      </Stack>

      <Box
        sx={{
          width: "90px",
          height: "40px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#f3f3f3",
          borderRadius: "5px",
          flexDirection: "column",
        }}
      >
        {isLoading ? (
          <Skeleton variant="rounded" width="100%" height="100%" />
        ) : (
          <Typography variant="caption" fontWeight="600" color={"#333"} sx={{ textAlign: "center", fontSize: "16px" }}>
            {unit}
            {value.toFixed(2)}
          </Typography>
        )}
      </Box>
    </ButtonBase>,
  ];
}

export function LegendValue({ name, value, icon, unit }) {
  const innerSx = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    backgroundColor: "white",
    borderRadius: 2,
    width: 100,
    padding: 1,
  };
  return (
    <Box sx={innerSx}>
      <Typography variant="caption" fontWeight="bold" color="rgb(170,170,170)">
        {name}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={"5px"}>
        {icon}
        <Typography variant="h6" fontWeight="bold" color={"#333"}>
          {value ? unit + value : "N/A"}
        </Typography>
      </Stack>
    </Box>
  );
}
